import React, { useEffect, useRef, useState } from 'react'
import cl from 'classnames'
import { useBreakpoints } from 'react-breakpoints-hook'

import styles from './styles.module.scss'
import { ImageOptimization } from '@components/image-optimization'
import Link from 'next/link'
import { AdsIndexDto } from '../../types/ads'

export type AdConfigProps = {
  imageWeb?: string
  imageMobile?: string
  linkTo?: string
  isScript?: boolean
  script?: string
}

interface SponsorBannerProps {
  className?: string
  config?: AdConfigProps
  ads?: AdsIndexDto[]
  code?: string
}

export const SponsorBanner = ({
  className,
  config,
  ads = [],
  code
}: SponsorBannerProps) => {
  const scriptContainerRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)
  const { sm } = useBreakpoints({
    sm: { min: 1, max: 640 },
    lg: { min: 641, max: null }
  })

  useEffect(() => {
    setMounted(true)
  }, [])

  // Se foi passado ads e code, procura pelo anúncio correspondente
  let adConfig = config

  if (!adConfig && code && ads.length > 0) {
    const foundAd = ads.find(ad => ad.code === code)
    if (foundAd) {
      adConfig = {
        imageWeb: foundAd.imageWeb || '',
        imageMobile: foundAd.imageMobile || '',
        linkTo: foundAd.linkTo || '#',
        isScript: foundAd.isScript,
        script: foundAd.script
      }
    }
  }

  // Se for script, renderiza o script
  useEffect(() => {
    if (
      !adConfig?.isScript ||
      !adConfig?.script ||
      !scriptContainerRef.current
    ) {
      return
    }

    const container = scriptContainerRef.current

    // Extrai apenas a div do script (sem o <script> tag)
    // Remove aspas extras que podem vir da API
    const cleanScript = adConfig.script.replace(/^"|"$/g, '')
    const divMatch = cleanScript.match(/<div[^>]*id=['"]([^'"]+)['"][^>]*>/)

    if (divMatch) {
      const fullDivMatch = cleanScript.match(
        /(<div[^>]*id=['"][^'"]+['"][^>]*>[\s\S]*?<\/div>)/i
      )
      const adId = divMatch[1]

      // Adiciona apenas a div (sem o script)
      if (fullDivMatch) {
        // Remove script tags se existirem
        const divOnly = fullDivMatch[1].replace(
          /<script[\s\S]*?<\/script>/gi,
          ''
        )
        container.innerHTML = divOnly
      }

      if (typeof window !== 'undefined') {
        ;(window as any).googletag = (window as any).googletag || { cmd: [] }
        const googletag = (window as any).googletag

        googletag.cmd.push(function () {
          try {
            const slots = googletag.pubads().getSlots()
            const slot = slots.find((s: any) => s.getSlotElementId() === adId)

            if (slot) {
              const adDiv = document.getElementById(adId)
              const alreadyDisplayed = adDiv?.getAttribute(
                'data-google-query-id'
              )

              if (alreadyDisplayed) {
                googletag.pubads().refresh([slot])
              } else {
                googletag.display(adId)
              }
            } else {
              console.warn(`[GPT] Slot não encontrado para: ${adId}`)
            }
          } catch (error) {
            console.error('[GPT] Erro ao exibir anúncio:', error)
          }
        })
      }
    } else {
      console.error(
        '[GPT] Não foi possível extrair o ID da div do script:',
        adConfig.script
      )
    }
  }, [adConfig?.isScript, adConfig?.script])

  if (!adConfig) {
    return null
  }

  if (adConfig.isScript && adConfig.script) {
    return (
      <div
        ref={scriptContainerRef}
        className={cl(className, styles.adContainer, 'pb-6')}
      />
    )
  }

  // Usar breakpoint só após montagem para evitar hydration mismatch (servidor vs cliente)
  const useMobileImage = mounted && sm
  const currentImageByResolution = useMobileImage
    ? adConfig?.imageMobile
    : adConfig?.imageWeb

  if (!currentImageByResolution) {
    return null
  }

  return (
    <Link
      href={adConfig?.linkTo || '#'}
      target="_blank"
      rel="noreferrer"
      className={cl(className, styles.adContainer)}
      aria-label="Anúncio patrocinado - Clique para saber mais"
    >
      {useMobileImage ? (
        <ImageOptimization
          className={styles.imageMobile}
          src={currentImageByResolution}
          alt="Banner publicitário para dispositivos móveis"
        />
      ) : (
        <ImageOptimization
          className={styles.imageWeb}
          src={currentImageByResolution}
          alt="Banner publicitário para desktop"
        />
      )}
    </Link>
  )
}
